import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress, Chip } from "@mui/material";
import moment from "moment";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CoffeeIcon from "@mui/icons-material/Coffee";
import TimerIcon from "@mui/icons-material/Timer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  useAttendancePunches,
  usePostAttendancePunch
} from "../../../hooks/query/useAttendancePunches";
import { UserMode } from "../../../interfaces/User";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { User, UserCreds } from "../../../interfaces/UserContext";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import { TAttendancePunchType } from "../../../interfaces/AttendanceInterface";
import { AttendancePunchType, AttendancePunchSource } from "../../../constants/attendance";

interface AttendancePunchSectionProps {
  user: User;
  userCreds: UserCreds;
  resourceCentre: ResourceCentre;
}

const getPunchLabel = (type: string) => {
  switch (type) {
    case AttendancePunchType.CHECK_IN:
      return "Check In";
    case AttendancePunchType.CHECK_OUT:
      return "Check Out";
    case AttendancePunchType.BREAK_IN:
      return "Break In";
    case AttendancePunchType.BREAK_OUT:
      return "Break Out";
    default:
      return type;
  }
};

// eslint-disable-next-line import/prefer-default-export
export function AttendancePunchSection({
  user,
  userCreds,
  resourceCentre
}: AttendancePunchSectionProps): React.JSX.Element {
  const today = moment().format("YYYY-MM-DD");

  const { mutate: postPunch, isLoading: isPunching } = usePostAttendancePunch();
  const { data: punches = [], isLoading: punchesLoading } = useAttendancePunches({
    employeeIds: userCreds.authenticable === UserMode.Employee ? [userCreds.authenticableId] : [],
    serviceProviderIds:
      userCreds.authenticable === UserMode.ServiceProvider ? [userCreds.authenticableId] : [],
    fromDate: today,
    toDate: today
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: TAttendancePunchType | null;
  }>({ open: false, type: null });

  // Sort punches by time descending to get the latest state
  const sortedPunches = [...punches].sort((a, b) => moment(b.punchedAt).diff(moment(a.punchedAt)));
  const lastPunch = sortedPunches[0];

  const handlePunch = () => {
    if (!confirmDialog.type || !resourceCentre?.id) return;

    const now = moment();

    postPunch({
      resourceCentreId: resourceCentre.id,
      punchDate: now.format("YYYY-MM-DD"),
      punchTime: now.format("HH:mm:ss"),
      punchType: confirmDialog.type,
      punchSource: AttendancePunchSource.MANUAL,
      employeeId:
        userCreds.authenticable === UserMode.Employee ? userCreds.authenticableId : undefined,
      serviceProviderId:
        userCreds.authenticable === UserMode.ServiceProvider ? userCreds.authenticableId : undefined
    });
    setConfirmDialog({ open: false, type: null });
  };

  if (punchesLoading) {
    return (
      <Box sx={{ py: 2, display: "flex", justifyContent: "center", width: "100%" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const renderButtons = () => {
    if (!lastPunch) {
      return (
        <Button
          variant="contained"
          color="primary"
          startIcon={<LoginIcon />}
          onClick={() => setConfirmDialog({ open: true, type: AttendancePunchType.CHECK_IN })}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          Check In
        </Button>
      );
    }

    if (lastPunch.punchType === AttendancePunchType.CHECK_OUT) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography fontWeight="500" color="success.main">
            Shift Completed
          </Typography>
        </Box>
      );
    }

    if (lastPunch.punchType === AttendancePunchType.BREAK_OUT) {
      return (
        <Button
          variant="outlined"
          startIcon={<TimerIcon />}
          onClick={() => setConfirmDialog({ open: true, type: AttendancePunchType.BREAK_IN })}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3, borderColor: "divider" }}
        >
          Break In
        </Button>
      );
    }

    // Default state: Checked in or Break in
    return (
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CoffeeIcon />}
          onClick={() => setConfirmDialog({ open: true, type: AttendancePunchType.BREAK_OUT })}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3, borderColor: "divider" }}
        >
          Break Out
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => setConfirmDialog({ open: true, type: AttendancePunchType.CHECK_OUT })}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          Check Out
        </Button>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        py: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        borderBottom: "1px solid #ededed"
      }}
    >
      <Box>
        <Typography color="primary" sx={{ fontWeight: "600" }}>
          My Profile
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: "700", mt: 0.5 }}>
          Welcome back, {user?.firstName || "Who are you?"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Track shifts, request leaves and claim comp-offs without leaving this page.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
        {renderButtons()}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", mt: 1 }}>
          {[...punches]
            .sort((a, b) => moment(a.punchedAt).diff(moment(b.punchedAt)))
            .map((p) => (
              <Chip
                key={p.id}
                label={`${getPunchLabel(p.punchType)} · ${moment(p.punchedAt).format("hh:mm A")}`}
                size="small"
                variant="outlined"
                sx={{
                  height: "24px",
                  fontSize: "0.7rem",
                  borderColor: "divider",
                  color: "text.secondary",
                  backgroundColor: "rgba(0,0,0,0.02)"
                }}
              />
            ))}
        </Box>
      </Box>

      {confirmDialog.open && (
        <OkhatiDialog
          title={`Confirm ${getPunchLabel(confirmDialog.type || "")}`}
          description={
            <Box>
              <Typography>
                Are you sure you want to <strong>{getPunchLabel(confirmDialog.type || "")}</strong>{" "}
                at:
              </Typography>
              <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                {moment().format("hh:mm:ss A")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {moment().format("MMMM D, YYYY")}
              </Typography>
            </Box>
          }
          next={handlePunch}
          cancel={() => setConfirmDialog({ open: false, type: null })}
          readMode={false}
          isLoading={isPunching}
          nextButtonText="Confirm"
        />
      )}
    </Box>
  );
}
